import { ComponentType, JSXElementConstructor, ReactElement } from "react";
import { Image, Text, View, XStack, YStack } from "tamagui";
import { Pill } from "../../design-system/Pill";
import { FlatList } from "react-native";
import { ArrowRightIcon } from "../../design-system/icons/ArrowRightIcon";
import { getFeet, getMiles } from "../../../features/Routes/utils";
import { useApiQuery } from "../../../../lib/api";
import { Loader } from "../../design-system/Loader";
import type { Route } from "../../../types/route";

type RouteItem = {
  node: Route;
};

type RoutesListProps = {
  ListHeaderComponent?: ComponentType<unknown> | undefined;
  ListFooterComponent?:
    | ComponentType<unknown>
    | ReactElement<unknown, string | JSXElementConstructor<unknown>>
    | null
    | undefined;
  profileId: string;
  searchTerm: string;
  isSyncing?: boolean;
  onRefresh?: () => void;
  onRoutePress: (route: RouteItem) => void;
};

export const RoutesList = ({
  ListFooterComponent,
  ListHeaderComponent,
  profileId,
  searchTerm,
  isSyncing,
  onRefresh,
  onRoutePress,
}: RoutesListProps) => {
  const { data, isLoading } = useApiQuery<{ routes: Route[] }>(
    ["routes", profileId, searchTerm],
    `/api/v1/routes?profile_id=${profileId}&search=${encodeURIComponent(searchTerm)}`,
    { enabled: !!profileId }
  );

  const routes = (data?.routes ?? []).map((r) => ({ node: r }));

  if (isLoading) return <Loader />;

  const renderItem = ({ item }: { item: RouteItem }) => {
    return (
      <View px="$4" mb="$4">
        <YStack
          borderWidth={1}
          borderColor="black"
          borderRadius={15}
          bg="$brandParchment"
          onPress={() => onRoutePress(item)}
          pos="relative"
          pressStyle={{
            scaleX: 0.99,
            scaleY: 0.99,
            shadowOffset: { height: 1, width: 1 },
            shadowOpacity: 0.7,
          }}
          {...{
            shadowColor: "black",
            shadowOffset: { width: 2, height: 2 },
            shadowOpacity: 1,
            shadowRadius: 0,
          }}
        >
          <View
            w="100%"
            overflow="hidden"
            pos="relative"
            borderTopRightRadius={15}
            borderTopLeftRadius={15}
            borderBottomColor={"black"}
            borderBottomWidth={1}
          >
            <Image
              source={{ uri: item?.node.image_url ?? "" }}
              w="100%"
              resizeMode="cover"
              height={225}
            />
          </View>
          <YStack
            alignItems="flex-start"
            justifyContent="space-between"
            px="$4"
            pb={"$4"}
            pt="$4"
            gap="$2"
          >
            <Text fontFamily="$italicHeadingFont" fontSize={20}>
              {item.node.name}
            </Text>

            <XStack w="100%">
              <XStack gap="$2" flex={1}>
                <Pill
                  bg="$brandYellow"
                  color="black"
                  borderColor="black"
                  text={`${getMiles(item?.node.distance ?? 0) ?? ""} mi`}
                />
                <Pill
                  bg="$brandYellow"
                  color="black"
                  borderColor="black"
                  text={`${getFeet(item.node?.elevation ?? 0)} ft`}
                />
              </XStack>

              <ArrowRightIcon width={20} />
            </XStack>
          </YStack>
        </YStack>
      </View>
    );
  };

  return (
    <FlatList
      refreshing={isSyncing}
      onRefresh={onRefresh}
      data={routes}
      keyExtractor={(item) => item.node.id}
      contentContainerStyle={{ paddingBottom: 16 }}
      ItemSeparatorComponent={() => <YStack mt="$4" />}
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={
        routes?.length && routes?.length > 0 ? null : ListFooterComponent
      }
      renderItem={renderItem}
    />
  );
};
